"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import bgAgenticAiImage from "@/assets/images/agentic-ai-area/agentic-ai-bg.svg";
import agenticRoboImage from "@/assets/images/agentic-ai-area/agentic-robo.svg";
import sendIcon from "@/assets/images/agentic-ai-area/send.svg";
import CommonTitle from "@/components/common/CommonTitle";
import CommonSubtitle from "@/components/common/CommonSubtitle";

const PLACEHOLDER_PHRASES = [
  "Summarize this 100-page market research doc",
  "Create a sprint plan for the Q3 mobile launch",
  "Find the latest API documentation on Confluence",
  "Draft a weekly status update for the engineering team",
  "What are the critical blockers in the roadmap?",
];

const TYPING_SPEED_MS = 10;
const DELETING_SPEED_MS = 0;
const PAUSE_AFTER_TYPING_MS = 2000;
const PAUSE_AFTER_DELETING_MS = 500;

const AgenticAiArea = () => {
  const [displayedText, setDisplayedText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!showPlaceholder) return;

    const phrase = PLACEHOLDER_PHRASES[phraseIndex];

    let delay: number;
    if (!isDeleting && displayedText.length < phrase.length) {
      delay = TYPING_SPEED_MS;
    } else if (!isDeleting && displayedText.length === phrase.length) {
      delay = PAUSE_AFTER_TYPING_MS;
    } else if (isDeleting && displayedText.length > 0) {
      delay = DELETING_SPEED_MS;
    } else {
      delay = PAUSE_AFTER_DELETING_MS;
    }

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayedText.length < phrase.length) {
          setDisplayedText(phrase.slice(0, displayedText.length + 1));
        } else {
          setIsDeleting(true);
        }
      } else {
        if (displayedText.length > 0) {
          setDisplayedText(displayedText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setPhraseIndex((i) => (i + 1) % PLACEHOLDER_PHRASES.length);
        }
      }
    }, delay);

    return () => clearTimeout(timeout);
  }, [displayedText, phraseIndex, isDeleting, showPlaceholder]);

  return (
    <section
      className="relative overflow-hidden bg-[#1D1244] py-12 lg:py-20"
      style={{
        backgroundImage: `url(${bgAgenticAiImage.src})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="container relative z-10">
        <div className="flex flex-col items-center lg:flex-row">
          <div className="flex flex-col px-4 text-white lg:w-[60%]">
            <CommonTitle
              color="#fff"
              fontSizeClass="text-[36px] lg:text-[48px] mb-2 max-w-[500px]"
              fontWeightClass="font-semibold"
              as="h2"
            >
              Meet Kroo: Your{" "}
              <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                Agentic AI
              </span>{" "}
            </CommonTitle>
            <CommonSubtitle color="#fff">
              The first-of-its-kind WorkOS that thinks, acts, and executes. Kroo
              AI unifies projects, docs, goals, and team chats into one
              intelligent engine.
            </CommonSubtitle>
            <div className="relative mt-6 w-full lg:mt-10">
              <div className="relative flex w-full items-center rounded-lg border border-white/10 bg-black/60 px-5 py-3 text-left shadow-[0_0_40px_rgba(0,0,0,0.6)] ring-1 ring-white/10 backdrop-blur-md">
                {showPlaceholder && !inputValue && (
                  <div
                    className="pointer-events-none absolute left-5 mr-3 flex w-[calc(100%-3.5rem)] items-center text-sm text-[#8B82B8] lg:text-lg"
                    aria-hidden
                  >
                    <span>{displayedText}</span>
                    <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-[#8B82B8]" />
                  </div>
                )}
                <input
                  id="agentic-ai-prompt"
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onFocus={() => setShowPlaceholder(false)}
                  onBlur={() => setShowPlaceholder(true)}
                  className="relative z-10 mr-3 w-full bg-transparent text-sm text-white outline-none placeholder:text-transparent"
                  placeholder=" "
                  aria-label="Ask the AI agent a question"
                />
                <button
                  type="button"
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center"
                >
                  <span className="sr-only">Send</span>
                  <Image
                    src={sendIcon}
                    alt="Send message to AI"
                    className="h-10 w-10 text-white"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="relative flex h-[380px] w-full items-center justify-end lg:w-[40%]">
            <Image
              src={agenticRoboImage}
              alt="Kroolo Agentic AI - Conversational assistant"
              width={400}
              height={400}
              priority
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgenticAiArea;
